const DEFAULT_PRIMARY_HEX = '#059669' // emerald-600-ish
const DEFAULT_ACCENT_HEX = '#0ea5e9' // sky-500

function clampInt(value, min, max) {
	return Math.min(max, Math.max(min, Math.round(value)))
}

function normalizeHex(hex) {
	if (!hex) return null
	const value = String(hex).trim()

	if (value.startsWith('#') && value.length === 4) {
		// #rgb -> #rrggbb
		const r = value[1]
		const g = value[2]
		const b = value[3]
		return `#${r}${r}${g}${g}${b}${b}`
	}

	if (value.startsWith('#') && value.length === 7) return value
	return null
}

function hexToRgb(hex) {
	const normalized = normalizeHex(hex)
	if (!normalized) return null

	const r = parseInt(normalized.slice(1, 3), 16)
	const g = parseInt(normalized.slice(3, 5), 16)
	const b = parseInt(normalized.slice(5, 7), 16)

	return { r, g, b }
}

function mixRgb(rgb_a, rgb_b, weight) {
	const w = Math.min(1, Math.max(0, weight))
	return {
		r: clampInt(rgb_a.r + (rgb_b.r - rgb_a.r) * w, 0, 255),
		g: clampInt(rgb_a.g + (rgb_b.g - rgb_a.g) * w, 0, 255),
		b: clampInt(rgb_a.b + (rgb_b.b - rgb_a.b) * w, 0, 255),
	}
}

function rgbToCss(rgb) {
	return `${rgb.r} ${rgb.g} ${rgb.b}`
}

export function generatePaletteFromHex(base_hex) {
	const base_rgb = hexToRgb(base_hex)
	if (!base_rgb) return null

	const white = { r: 255, g: 255, b: 255 }
	const black = { r: 0, g: 0, b: 0 }

	// Simple palette: tint (towards white) for 50-400, base for 500, shade (towards black) for 600-900
	const palette = {
		50: rgbToCss(mixRgb(base_rgb, white, 0.9)),
		100: rgbToCss(mixRgb(base_rgb, white, 0.8)),
		200: rgbToCss(mixRgb(base_rgb, white, 0.6)),
		300: rgbToCss(mixRgb(base_rgb, white, 0.4)),
		400: rgbToCss(mixRgb(base_rgb, white, 0.2)),
		500: rgbToCss(base_rgb),
		600: rgbToCss(mixRgb(base_rgb, black, 0.2)),
		700: rgbToCss(mixRgb(base_rgb, black, 0.35)),
		800: rgbToCss(mixRgb(base_rgb, black, 0.5)),
		900: rgbToCss(mixRgb(base_rgb, black, 0.65)),
	}

	return palette
}

export function applyThemeToDom(theme) {
	if (typeof document === 'undefined') return

	const is_dark = theme?.color_mode === 'dark'
	const color_mode = is_dark ? 'dark' : 'light'

	const root_el = document.documentElement
	root_el.dataset.colorMode = color_mode
	root_el.classList.toggle('dark', is_dark)

	const primary_hex = normalizeHex(theme?.primary_color) || DEFAULT_PRIMARY_HEX
	const accent_hex = normalizeHex(theme?.accent_color) || DEFAULT_ACCENT_HEX

	const primary_palette = generatePaletteFromHex(primary_hex)
	const accent_palette = generatePaletteFromHex(accent_hex)

	const root_style = root_el.style

	// App-level semantic tokens (stored as "r g b" for convenient rgb(var(--x)) usage)
	root_style.setProperty('--app-bg', is_dark ? '15 17 21' : '255 255 255')
	root_style.setProperty('--app-surface', is_dark ? '22 27 34' : '255 255 255')
	root_style.setProperty('--app-text', is_dark ? '229 231 235' : '15 23 42')
	root_style.setProperty('--app-text-muted', is_dark ? '156 163 175' : '100 116 139')
	root_style.setProperty('--app-border', is_dark ? '51 65 85' : '226 232 240')

	Object.entries(primary_palette || {}).forEach(([shade, rgb]) => {
		root_style.setProperty(`--theme-primary-${shade}`, rgb)
	})

	Object.entries(accent_palette || {}).forEach(([shade, rgb]) => {
		root_style.setProperty(`--theme-accent-${shade}`, rgb)
	})

	const favicon_url = theme?.favicon_url
	if (favicon_url) {
		const icon_links = document.querySelectorAll("link[rel~='icon']")
		icon_links.forEach((icon_link) => {
			icon_link.href = favicon_url
		})

		const apple_touch_links = document.querySelectorAll("link[rel='apple-touch-icon']")
		apple_touch_links.forEach((apple_touch_link) => {
			apple_touch_link.href = favicon_url
		})
	}
}
